// React core
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";

import { styled, Button, Tooltip } from "@monolith-forensics/monolith-ui";
import { ArrowLeft } from "lucide-react";

import { useQueries, useQueryClient } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/usePermissions";
import OfficesApi from "@/api/offices/index.js";

import AccessDeniedMessage from "@/components/AccessDeniedMessage.js";
import {
  UpdateOfficeForm,
  DeleteOfficeModal,
  useOfficesListQuery,
  LAST_OFFICE_MESSAGE,
  OFFICE_COUNT_UNAVAILABLE_MESSAGE,
} from "@/Features/Offices";

const OfficeDetails = () => {
  const { office_id } = useParams();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const queryClient = useQueryClient();

  const hasReadPermission = hasPermission(
    MONOLITH_PERMISSIONS.OFFICES_READ_ALL
  );

  // Monolith needs at least one office — deleting the last one orphans every
  // user and storage location assigned to it. The API layer resolves with
  // { success: false } instead of rejecting, so a failed fetch leaves a
  // non-array in data — Array.isArray is what separates "loaded" from
  // "could not read".
  const { data: offices, isPending: officesPending } = useOfficesListQuery();
  const officesLoaded = Array.isArray(offices);
  const isLastOffice = officesLoaded && offices.length <= 1;

  // Explains a disabled Delete button. Stays undefined while the list is in
  // flight so an ordinary page load doesn't flash a reason that isn't true.
  const deleteBlockedMessage = isLastOffice
    ? LAST_OFFICE_MESSAGE
    : !officesLoaded && !officesPending
    ? OFFICE_COUNT_UNAVAILABLE_MESSAGE
    : undefined;

  const result = useQueries({
    queries: [
      {
        queryKey: ["offices", "list", { office_id }],
        queryFn: () => OfficesApi.getOffices({ office_id }),
        enabled: hasReadPermission,
      },
    ],
  });

  const handleOfficeUpdate = () => {
    queryClient.invalidateQueries({
      queryKey: ["offices", "list", { office_id }],
    });
  };

  const isDone = result.every((query) => query.isFetched);

  const [office] = result[0]?.data || [];

  if (!hasReadPermission) {
    return <AccessDeniedMessage />;
  }

  return (
    <>
      <Helmet title="Office Details" />
      {isDone && (
        <>
          <PageHeader>
            <Link to="/offices">
              <BackButton variant="outlined" size="xs" title="Back to Offices">
                <ArrowLeft size={16} />
              </BackButton>
            </Link>

            <OfficeTitle>
              {office.office_name}
              <OfficeSubtitle>
                {office.office_city || "City"},{" "}
                {office.office_region || "State/Province"}
              </OfficeSubtitle>
            </OfficeTitle>
          </PageHeader>

          <ContentContainer>
            <DetailsCard>
              <DetailsContent>
                <DetailsLayout>
                  <div>
                    <SectionTitle>Office Details</SectionTitle>
                    <SectionDescription></SectionDescription>
                  </div>
                  <FormContainer>
                    {hasPermission(MONOLITH_PERMISSIONS.OFFICES_UPDATE) ? (
                      <UpdateOfficeForm
                        defaultFormData={{
                          office_name: office.office_name,
                          office_address: office.office_address,
                          office_city: office.office_city,
                          office_region: office.office_region,
                          office_postal_code: office.office_postal_code,
                          office_phone: office.office_phone,
                          manager_id: office.manager_id,
                          office_id: office.office_id,
                        }}
                        onSubmit={handleOfficeUpdate}
                        onCancel={() => {}} // Not needed outside a modal context
                      />
                    ) : (
                      <ReadOnlyDetails>
                        <DetailItem>
                          <strong>Office Name:</strong> {office.office_name}
                        </DetailItem>
                        <DetailItem>
                          <strong>Address:</strong>{" "}
                          {office.office_address || "N/A"}
                        </DetailItem>
                        <DetailItem>
                          <strong>City:</strong> {office.office_city || "N/A"}
                        </DetailItem>
                        <DetailItem>
                          <strong>State/Province:</strong>{" "}
                          {office.office_region || "N/A"}
                        </DetailItem>
                        <DetailItem>
                          <strong>Postal Code:</strong>{" "}
                          {office.office_postal_code || "N/A"}
                        </DetailItem>
                        <DetailItem>
                          <strong>Phone:</strong> {office.office_phone || "N/A"}
                        </DetailItem>
                        <DetailItem>
                          <strong>Manager:</strong>{" "}
                          {office.manager?.full_name || "N/A"}
                        </DetailItem>
                      </ReadOnlyDetails>
                    )}
                  </FormContainer>
                </DetailsLayout>
              </DetailsContent>
            </DetailsCard>

            {hasPermission() && (
              <DeleteSection>
                <div>
                  <SectionTitle>Delete Office</SectionTitle>
                  <SectionDescription>
                    Delete this office from Monolith.
                  </SectionDescription>
                </div>
                <div>
                  <Tooltip content={deleteBlockedMessage}>
                    {/* A disabled button fires no pointer events, so the span
                        is what the tooltip anchors its hover to. */}
                    <span style={{ display: "inline-block" }}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="xs"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={
                          !hasPermission(MONOLITH_PERMISSIONS.OFFICES_DELETE) ||
                          !officesLoaded ||
                          isLastOffice
                        }
                      >
                        Delete Office
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </DeleteSection>
            )}
          </ContentContainer>

          <DeleteOfficeModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onSubmit={() => {
              queryClient.invalidateQueries({ queryKey: ["offices"] });
            }}
            office={office}
          />
        </>
      )}
    </>
  );
};

// Styled Components
const PageHeader = styled.div`
  padding: 10px 5px;
  margin: 0;
  display: flex;
  align-content: center;
  align-items: center;
`;

const BackButton = styled(Button)`
  min-width: fit-content;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
`;

const OfficeTitle = styled.div`
  font-size: x-large;
  margin-left: 10px;
`;

const OfficeSubtitle = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: small;
`;

const ContentContainer = styled.div`
  box-shadow: none;
`;

const DetailsCard = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 5px;
`;

const DetailsContent = styled.div`
  padding: 40px;
  text-align: left;
`;

const DetailsLayout = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 750px;
`;

const SectionTitle = styled.div`
  font-size: large;
`;

const SectionDescription = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const FormContainer = styled.div`
  width: 500px;
`;

const ReadOnlyDetails = styled.div`
  padding: 20px 0;
`;

const DetailItem = styled.div`
  margin-bottom: 16px;
`;

const DeleteSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 40px;
  margin-top: 25px;
  text-align: left;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 5px;
`;

export default OfficeDetails;
